import { useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

const useInfiniteList = ({
  queryKey,
  apiCall,
  limit = 50,
  extraPayload = {},
  // Optional (lastPage, allPages) => next page number | undefined. Screens that
  // don't pass it keep the previous behaviour (a single page).
  getNextPageParam: customNextPageParam,
  onSuccess,
  onError // Add this to destructuring
}) => {
  const query = useInfiniteQuery({
    queryKey: [...(Array.isArray(queryKey) ? queryKey : [queryKey]), extraPayload],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await apiCall({
          page: pageParam,
          limit,
          ...extraPayload,
        });

        // 🔥 ONLY CACHE IF 200
        if (response?.status === 200) {
          return response;
        }

        // If not 200, throw the response so React Query knows it's an error
        throw response; 
      } catch (error) {
        // Re-throw so the error state is triggered and nothing is cached
        throw error;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (typeof customNextPageParam === "function") {
        return lastPage?.status === 200 ? customNextPageParam(lastPage, allPages) : undefined;
      }
      if (!lastPage || lastPage.status !== 200) return undefined;
      const currentData = Array.isArray(lastPage.data) ? lastPage.data : [];
      return currentData.length >= limit ? undefined : undefined; // Adjusted based on your limit logic
    },
    staleTime: 1000 * 60 * 5,
    retry: false, // Prevents retrying a 500 error multiple times
  });

  // Handle side effects manually since v5 doesn't support them in config
  useEffect(() => {
    if (query.isSuccess && query.data && typeof onSuccess === "function") {
      onSuccess(query.data);
    }
  }, [query.data, query.isSuccess, onSuccess]);

  useEffect(() => {
    if (query.isError && typeof onError === "function") {
      onError(query.error);
    }
  }, [query.error, query.isError, onError]);

  return query;
};

export default useInfiniteList;